import { useQueryClient } from '@tanstack/react-query'
import { generatePath, useNavigate } from 'react-router-dom'

import { PageContainer } from '@/customer/components/page-container'
import { PetForm } from '@/customer/components/pet/pet.form'
import { Button } from '@/customer/components/ui/button'
import { useCreatePet } from '@/customer/hooks/use-create-pet'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { meQueryKey } from '@/customer/hooks/use-get-me'
import { routes } from '@/customer/routes'

/**
 * Portal "Add a pet" page.
 *
 * Portal-native counterpart to the register-flow `PetRegistrationPage`
 * (step 2). Reuses the same `<PetForm>` so validation and copy stay in
 * lockstep; the only differences are the surrounding chrome (portal
 * layout, no step progress) and the destination on submit - portal
 * add-pet hands off into the portal-native plan picker at
 * `/account/pets/:id/plan`, which in turn hands off to the portal
 * checkout page at `/account/pets/:id/checkout/:plan`. The whole
 * signup-second-pet flow lives under `/account` end to end.
 */
export function AddPetPage() {
  useDocumentTitle('Add a pet')

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const createPet = useCreatePet()

  return (
    <PageContainer
      backLink={{ to: routes.account.pets.index, label: 'Back to your pets' }}
      headline={[
        { text: 'Add a', color: 'pink' },
        { text: 'new pet', color: 'blue' },
      ]}
    >
      <PetForm
        onSubmit={async (value) => {
          const { patient } = await createPet.mutateAsync({
            name: value.petName,
            species: value.species,
            sex: value.sex,
            breed: value.breed,
            dateOfBirth: value.dateOfBirth,
          })
          await queryClient.invalidateQueries({ queryKey: meQueryKey })
          navigate(generatePath(routes.account.pets.plan, { patientId: patient.id }))
        }}
      >
        {({ submit, canSubmit, isSubmitting }) => (
          <div className="flex md:justify-end">
            <Button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="w-full md:w-auto md:min-w-40"
            >
              {isSubmitting ? 'Adding…' : 'Continue'}
            </Button>
          </div>
        )}
      </PetForm>
    </PageContainer>
  )
}
